import * as React from 'react';
import Media from 'react-media';

import Imgix from 'js/components/Imgix';

import { Grid, breakpoints } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { MarketingContentFieldsFragment } from 'bundles/xddp/__generated__/contentfulTypes';
import type { StudentExperienceHighlightsProps, UserAgentProps } from 'bundles/xddp/types';

import 'css!bundles/xddp/components/__styles__/student-experience/StudentExperienceHighlights';

type HighlightNoVisual = Pick<MarketingContentFieldsFragment, 'title' | 'description'>;

const TextBox = ({ title, description }: HighlightNoVisual) => {
  return (
    <Grid item sm={12} md={7}>
      <h4 className="section-header">{title}</h4>
      <Markdown className="description" source={description} />
    </Grid>
  );
};

const MediaBox = ({ visual }: $TSFixMe) => {
  return (
    <Grid item sm={12} md={5}>
      <Imgix className="highlight-image" src={visual.url ?? ''} alt="" />
    </Grid>
  );
};

export const HighlightWithVisual = ({
  title,
  description,
  visual,
  textOnLeft,
  userAgent,
}: MarketingContentFieldsFragment & UserAgentProps) => {
  const mediaBox = <MediaBox visual={visual} />;
  const textBox = <TextBox title={title} description={description} />;

  return (
    <Media query={{ minWidth: breakpoints.values.md }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) =>
        matches ? (
          <Grid container className="rc-Section studentExperienceHighlight" alignItems="center" spacing={24}>
            {textOnLeft ? textBox : mediaBox}
            {textOnLeft ? mediaBox : textBox}
          </Grid>
        ) : (
          <Grid container className="rc-Section studentExperienceHighlight" alignItems="center" spacing={24}>
            {mediaBox}
            {textBox}
          </Grid>
        )
      }
    </Media>
  );
};

export const HighlightWithoutVisual = ({ title, description }: HighlightNoVisual) => {
  return (
    <Grid container className="rc-Section rc-HighlightWithoutVisual">
      <Grid item lg={12}>
        <h4 className="section-header">{title}</h4>
        <Markdown className="description" source={description} />
      </Grid>
    </Grid>
  );
};

const Highlight = ({
  highlightData,
  userAgent,
}: { highlightData: MarketingContentFieldsFragment } & UserAgentProps) => {
  const { title, description, visual, textOnLeft } = highlightData;

  if (visual) {
    return (
      <HighlightWithVisual
        userAgent={userAgent}
        title={title}
        description={description}
        visual={visual}
        textOnLeft={textOnLeft}
      />
    );
  }

  return <HighlightWithoutVisual title={title} description={description} />;
};

const StudentExperienceHighlights = ({
  highlights,
  overview,
  userAgent,
}: StudentExperienceHighlightsProps & UserAgentProps) => {
  if (!highlights || !highlights.length) {
    return null;
  }

  return (
    <div className="rc-StudentExperienceHighlights">
      <Markdown className="studentExperienceOverview rc-Section" source={overview} />
      {highlights
        .filter((highlightData): highlightData is MarketingContentFieldsFragment => highlightData !== null)
        .map((highlightData) => (
          <Highlight userAgent={userAgent} highlightData={highlightData} />
        ))}
    </div>
  );
};

export default React.memo(StudentExperienceHighlights);
